
import MBaseModal from "@/components/MBaseModal.vue";
import MButton from "@/components/MButton.vue";
import MSelectBox from "@/components/form/MSelectBox.vue";
import MTextField from "@/components/form/MTextField.vue";
import MMultiSelectBox from "@/components/form/MMultiSelectBox.vue";
import store from "@/store";
import { isPasswordLoginDisabledBySchool } from "@/entities/tutor_password_policy";
import { Options, Vue } from "vue-class-component";
import { Classroom } from "@/entities/classroom";
import { TutorAuthority } from "@/entities/tutor";
import {
  deleteTutor,
  TutorMeta,
  updateTutor,
  getExternalServiceEmailOfTutor
} from "@/api/tutor";
import { saveErrorLog } from "@/api/error";
import { validEmail } from "@/utils/email";

@Options({
  components: {
    MBaseModal,
    MButton,
    MSelectBox,
    MTextField,
    MMultiSelectBox
  },
  emits: ["close"],
  props: {
    tutor: Object,
    editTutor: Object,
    editTutorEmail: String,
    canEditEmail: Boolean,
    data: Object,
    classrooms: Array
  }
})
export default class MsEditModal extends Vue {
  name = "";
  authorityText = "一般";
  authorityTexts: string[] = [];
  selectClassroomIds: string[] = [];
  tutor!: TutorMeta;
  editTutor!: TutorMeta;
  editTutorEmail!: string;
  canEditEmail!: boolean;
  /** 入力したログイン用メールアドレス。触っていない間は null で、読み込んだアドレスを表示する */
  emailInput: string | null = null;
  passwordSettingUrl = `${window.location.origin}/setting_password`;
  classrooms: Classroom[] = [];
  googleAccountEmail: string | null = null;
  microSoftAccountEmail: string | null = null;
  allowBulkUpdateTutors = false;
  passwordLoginDisabled = false;
  validateEmail = validEmail;

  get isOwner(): boolean {
    return this.tutor.config.data.authority === "owner";
  }

  get isEditTutorAdmin(): boolean {
    return this.editTutor.config.data.authority === "admin";
  }

  /**
   * スクール単位のパスワードログインの無効（スーパー管理人が導入スクール管理画面で設定）が、
   * 選んでいる権限に効くか。オーナー・管理者には効かない（オーナーはこの画面で権限を変えないので、保存済みの権限で決める）
   */
  get passwordLoginLockedBySchool(): boolean {
    return isPasswordLoginDisabledBySchool(
      this.editTutor.config.data.authority === "owner"
        ? "owner"
        : this.authority,
      store.state.schoolConfig?.data
    );
  }

  get isSchool(): boolean {
    return store.state.isSchool;
  }

  get validGoogleAccountEmail(): boolean {
    if (!this.googleAccountEmail) return true;
    return this.validateEmail(this.googleAccountEmail);
  }

  get validMicroSoftAccountEmail(): boolean {
    if (!this.microSoftAccountEmail) return true;
    return this.validateEmail(this.microSoftAccountEmail);
  }

  get selectClassrooms(): Classroom[] {
    return this.classrooms.filter(item =>
      this.selectClassroomIds.includes(item.ref.id)
    );
  }

  get authority(): TutorAuthority {
    if (["管理者", "ICT担当者"].includes(this.authorityText)) {
      return "admin";
    } else if (this.authorityText === "責任者") {
      return "supervisor";
    } else if (["担当者", "先生"].includes(this.authorityText)) {
      return "employee";
    } else {
      return "general";
    }
  }

  get classroomsPlaceholder(): string {
    let text = `${this.selectClassrooms.length}教室`;
    if (this.selectClassrooms.length === 0) {
      return text;
    }
    text += ": ";
    text += this.selectClassrooms.map(item => item.data.name).join(", ");
    return text;
  }

  get validName() {
    return this.name.length > 0;
  }

  get loginEmail(): string {
    return this.emailInput ?? this.editTutorEmail ?? "";
  }

  get emailChanged(): boolean {
    return (
      this.canEditEmail &&
      !!this.editTutorEmail &&
      this.loginEmail.trim().toLowerCase() !== this.editTutorEmail.toLowerCase()
    );
  }

  get validLoginEmail(): boolean {
    return !this.emailChanged || this.validateEmail(this.loginEmail.trim());
  }

  get validData() {
    return (
      this.validName &&
      this.validLoginEmail &&
      this.validGoogleAccountEmail &&
      this.validMicroSoftAccountEmail
    );
  }

  changeClassroom(id: string) {
    if (this.selectClassroomIds.includes(id)) {
      this.selectClassroomIds = this.selectClassroomIds.filter(
        item => item !== id
      );
    } else {
      this.selectClassroomIds.push(id);
    }
  }

  translateErrorMessage(message: string): string {
    if (
      message.includes("This google account is already in use") ||
      message.includes("This Gmail is already in use") ||
      message.includes("This gmail is already in use")
    ) {
      return "すでに使われているGoogleアカウントです";
    } else if (
      message.includes("This microsoft account is already in use") ||
      message.includes("This microsoft email is already in use") ||
      message.includes("This MicrosoftEmail is already in use")
    ) {
      return "すでに使われているMicrosoftアカウントです";
    } else if (
      message.includes("このメールアドレスはすでに使われています") ||
      message.includes("This email is already in use")
    ) {
      return "このメールアドレスはすでに使われています";
    } else {
      return message;
    }
  }

  async update() {
    if (!this.validData) {
      return;
    }
    if (
      this.emailChanged &&
      !confirm(
        `ログインに使うメールアドレスを ${
          this.editTutorEmail
        } から ${this.loginEmail.trim()} に変更します。次回のログインから新しいメールアドレスを使い、パスワードの再設定メールも新しいアドレスに届きます。よろしいですか？`
      )
    ) {
      return;
    }

    store.commit("START_LOADING", "更新中...");

    try {
      await updateTutor(this.editTutor.main.ref.id, {
        name: this.name,
        ...(this.emailChanged ? { email: this.loginEmail.trim() } : {}),
        authority: this.authority,
        classrooms:
          this.authority === "general" ||
          this.authority === "employee" ||
          this.authority === "supervisor"
            ? this.selectClassroomIds
            : [],
        googleAccountEmail: this.googleAccountEmail ?? "",
        microSoftAccountEmail: this.microSoftAccountEmail ?? "",
        allowBulkUpdateTutors: this.allowBulkUpdateTutors,
        passwordLoginDisabled: this.passwordLoginDisabled
      });
      store.commit("END_LOADING");
    } catch (e) {
      store.commit("END_LOADING");
      alert(
        e instanceof Error
          ? this.translateErrorMessage(e.message ?? "unknown error")
          : e
      );
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to update tutor"
      );
      return;
    }

    this.$router.go(0);
  }

  async deleteTutor() {
    const confirmRes = confirm(
      "スタッフを本当に削除しますか？一度削除したスタッフを復元することはできません"
    );
    if (!confirmRes) {
      return;
    }

    store.commit("START_LOADING", "削除中...");
    try {
      await deleteTutor(this.editTutor.main.ref);
      store.commit("END_LOADING");
    } catch (e) {
      store.commit("END_LOADING");
      alert(e);
      await saveErrorLog(
        store.state.role,
        e.code,
        e.message,
        "Failed to delete tutor"
      );
      return;
    }

    this.$router.go(0);
  }

  close() {
    this.$emit("close");
  }

  async created() {
    this.name = this.editTutor.main.data.name;
    this.allowBulkUpdateTutors =
      this.editTutor.main.data.allowBulkUpdateTutors ?? false;
    this.passwordLoginDisabled =
      this.editTutor.config.data.passwordLoginDisabled ?? false;

    if (this.isSchool) {
      this.authorityText = "先生";
      if (this.editTutor.config.data.authority === "admin") {
        this.authorityText = "ICT担当者";
      } else if (this.editTutor.config.data.authority === "employee") {
        this.authorityText = "先生";
      }
      this.selectClassroomIds = this.editTutor.config.data.classrooms.map(
        item => item.id
      );
      if (this.tutor.config.data.authority === "owner") {
        this.authorityTexts = ["先生", "ICT担当者"];
      } else if (this.tutor.config.data.authority === "admin") {
        this.authorityTexts = ["先生"];
      } else if (this.tutor.config.data.authority === "supervisor") {
        this.authorityTexts = ["先生"];
      }
    } else {
      this.authorityText = "一般";
      if (this.editTutor.config.data.authority === "admin") {
        this.authorityText = "管理者";
      } else if (this.editTutor.config.data.authority === "supervisor") {
        this.authorityText = "責任者";
      } else if (this.editTutor.config.data.authority === "employee") {
        this.authorityText = "担当者";
      } else {
        this.authorityText = "一般";
      }
      this.selectClassroomIds = this.editTutor.config.data.classrooms.map(
        item => item.id
      );
      if (this.tutor.config.data.authority === "owner") {
        this.authorityTexts = ["一般", "担当者", "責任者", "管理者"];
      } else if (this.tutor.config.data.authority === "admin") {
        this.authorityTexts = ["一般", "担当者", "責任者"];
      } else if (this.tutor.config.data.authority === "supervisor") {
        this.authorityTexts = ["一般", "担当者"];
      } else {
        this.authorityTexts = ["一般"];
      }
    }

    this.googleAccountEmail =
      (await getExternalServiceEmailOfTutor(
        "google",
        this.editTutor.main.ref
      )) ?? "";
    this.microSoftAccountEmail =
      (await getExternalServiceEmailOfTutor(
        "microsoft",
        this.editTutor.main.ref
      )) ?? "";
  }
}
